import { rgba } from 'polished';
import { HTMLAttributes, KeyboardEvent, ReactNode } from 'react';
import styled from 'styled-components';

import { activateOnKeyDown, focusVisible } from '../helpers';
import { tokens } from '../theme/tokens';
import { Skeleton } from './Skeleton';
import { Heading3, Label } from './Text';

const Element = styled.div<{ $selected?: boolean; $clickable?: boolean }>`
	box-sizing: border-box;
	border-bottom: 1px solid ${tokens.border.subtle};
	background: ${(props) =>
		props.$selected ? rgba(props.theme.colors.primary, 0.08) : 'transparent'};
	cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};

	${(props) =>
		!props.$selected &&
		`
		&:hover {
			background: ${tokens.surface.hover};
		}
	`}

	${focusVisible}

	&:focus-visible {
		outline-offset: -2px;
	}
`;

const Row = styled.div<{ $small?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${(props) => (props.$small ? tokens.gap.list : tokens.gap.group)}px;
	padding: ${(props) =>
		props.$small
			? `${tokens.padding.compact}px ${tokens.padding.control}px`
			: `${tokens.gap.group}px ${tokens.padding.container}px`};
`;

const Main = styled.div<{ $small?: boolean }>`
	display: flex;
	flex: 1 1 auto;
	align-items: center;
	gap: ${(props) => (props.$small ? tokens.gap.list : tokens.gap.group)}px;
	min-width: 0;
`;

const IconSlot = styled.div<{ $small?: boolean }>`
	flex-shrink: 0;
	width: ${(props) => (props.$small ? 20 : 24)}px;
	height: ${(props) => (props.$small ? 20 : 24)}px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${tokens.icon.primary};

	svg {
		display: block;
		width: ${(props) => (props.$small ? 20 : 24)}px;
		height: ${(props) => (props.$small ? 20 : 24)}px;
		color: inherit;
	}

	> svg {
		fill: currentColor;

		path {
			fill: currentColor;
		}
	}
`;

const Copy = styled.div<{ $small?: boolean }>`
	display: flex;
	flex: 1 1 auto;
	flex-direction: column;
	align-items: flex-start;
	gap: ${(props) => (props.$small ? 2 : tokens.gap.inline)}px;
	min-width: 0;
`;

const MetaRow = styled.div<{ $small?: boolean }>`
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	gap: 0 ${tokens.gap.inline}px;
	width: 100%;
	min-width: 0;
	overflow: hidden;
	white-space: nowrap;
	color: ${tokens.text.secondary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${(props) =>
		props.$small ? tokens.typography.sizes.small : tokens.typography.sizes.body}px;
	font-weight: 450;
	letter-spacing: 0.14px;
	line-height: ${(props) =>
		props.$small ? tokens.typography.lineHeights.small : tokens.typography.lineHeights.body};
`;

const MetaAccent = styled.span<{ $tone: MetaAccentTone }>`
	color: ${(props) => (props.$tone === 'warning' ? tokens.warning.text : tokens.error.text)};
`;

type MetaAccentTone = 'error' | 'warning';

const Trailing = styled.div`
	display: flex;
	flex-shrink: 0;
	align-items: center;
	gap: ${tokens.gap.element}px;
`;

function ListItemComponent({
	icon,
	title,
	body,
	meta,
	metaAccent,
	metaAccentTone = 'error',
	trailing,
	selected,
	small,
	onClick,
	children,
	...rest
}: {
	icon?: ReactNode;
	title: ReactNode;
	/** Rendered between the title and the meta row, e.g. an avatar stack. */
	body?: ReactNode;
	meta?: ReactNode;
	metaAccent?: ReactNode;
	metaAccentTone?: MetaAccentTone;
	trailing?: ReactNode;
	selected?: boolean;
	small?: boolean;
	children?: ReactNode;
} & Omit<HTMLAttributes<HTMLDivElement>, 'title'>) {
	const showMeta = (meta != null && meta !== '') || (metaAccent != null && metaAccent !== '');
	const Title = small ? Label : Heading3;
	const clickable = !!onClick;

	// The row can hold its own buttons, so it stays a div with a button role
	// rather than a real button, and only reacts to keys aimed at the row itself.
	function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
		if (event.target !== event.currentTarget) {
			return;
		}

		const element = event.currentTarget;
		activateOnKeyDown(event, () => element.click());
	}

	return (
		<Element
			$selected={selected}
			$clickable={clickable}
			onClick={onClick}
			{...rest}
			role={clickable ? 'button' : rest.role}
			tabIndex={clickable ? 0 : rest.tabIndex}
			onKeyDown={clickable ? handleKeyDown : rest.onKeyDown}
			data-list-item
		>
			<Row $small={small}>
				<Main $small={small}>
					{icon && <IconSlot $small={small}>{icon}</IconSlot>}
					<Copy $small={small}>
						<Title>{title}</Title>
						{body}
						{showMeta && (
							<MetaRow $small={small}>
								{meta}
								{metaAccent != null && metaAccent !== '' && (
									<MetaAccent $tone={metaAccentTone}>{metaAccent}</MetaAccent>
								)}
							</MetaRow>
						)}
					</Copy>
				</Main>
				{trailing && <Trailing>{trailing}</Trailing>}
			</Row>
			{children}
		</Element>
	);
}

function ListItemSkeleton(props: {
	/** Placeholder for the `body` slot, e.g. `Avatar.Skeleton`s. */
	body?: ReactNode;
	titleWidth?: number;
	metaWidth?: number;
}) {
	return (
		<Element data-list-item>
			<Row>
				<Main>
					<Copy>
						<Heading3.Skeleton width={props.titleWidth ?? 160} />
						{props.body}
						<Skeleton.Box style={{ width: props.metaWidth ?? 280, height: 16 }} />
					</Copy>
				</Main>
			</Row>
		</Element>
	);
}

export const ListItem = Skeleton(ListItemComponent, ListItemSkeleton);
